import { useEffect, useRef, useState } from 'react';

interface UseScrollManagementOptions {
  fixedHeaderSelector?: string; // Selector for the fixed header to calculate content margin
  inventoryContainerSelector?: string; // Selector for the main scrollable content
}

export const useScrollManagement = (options?: UseScrollManagementOptions) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<number | null>(null);
  const scrollPositionRef = useRef(0);

  // Effect for detecting scrolling and adding body class
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);
      if (scrollTimeoutRef.current) window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = window.setTimeout(() => {
        setIsScrolling(false);
        scrollTimeoutRef.current = null;
      }, 150);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) window.clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (isScrolling) document.body.classList.add('scrolling');
    else document.body.classList.remove('scrolling');
  }, [isScrolling]);

  // Effect for layout shift prevention styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .room-inventory-item { contain: layout style; min-height: 100px; /* Adjust as needed */ }
      .room-inventory-section { contain: layout style; }
      html { overflow-anchor: none; }
    `;
    document.head.appendChild(style);
    return () => { if (style.parentNode) document.head.removeChild(style); };
  }, []);

  // Effect for fixed header styling adjustment (if selector is provided)
  useEffect(() => {
    if (options?.fixedHeaderSelector) {
      const headerStyle = document.createElement('style');
      const headerEl = document.querySelector(options.fixedHeaderSelector);
      const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 70; // Default or fallback

      headerStyle.textContent = `
        /* Ensure .inventory-header is styled with position:fixed, top:0, etc. in your CSS */
        .inventory-content-wrapper { /* Apply this class to the main content area */
          padding-top: ${headerHeight + 16}px; /* header height + some padding */
        }
        @media (max-width: 768px) {
          .inventory-content-wrapper {
            padding-top: ${headerHeight + 8}px; /* Adjust for mobile if header is smaller */
          }
        }
      `;
      document.head.appendChild(headerStyle);
      return () => { if (headerStyle.parentNode) document.head.removeChild(headerStyle); };
    }
  }, [options?.fixedHeaderSelector]);

  // Note: The MutationObserver based scroll restoration was complex and might be redundant
  // with the localStorage one. If specific issues arise, it can be re-evaluated.
}; 